import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './InteractiveMap.css';

interface SpawnData {
  Pokemon: string;
  MinLVL: number;
  MaxLVL: number;
  Tier: string;
  MemberOnly: boolean;
  Map: string;
  Daytime?: number[];
  Item?: string;
}

interface MapRegion {
  name: string;
  coords: string;
  shape: 'rect' | 'poly' | 'circle';
  subzones: string[] | null;
  groupName?: string;
  groupColor?: string;
}

interface RegionGroup {
  color: string;
  regions: MapRegion[];
}

interface MapRegionsData {
  [key: string]: RegionGroup;
}



const MapRegionEditor: React.FC = () => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [spawnData, setSpawnData] = useState<SpawnData[]>([]);
  const [loading, setLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [minScale, setMinScale] = useState(0.5);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [debugCoords, setDebugCoords] = useState({ x: 0, y: 0 });
  const [showDebug, setShowDebug] = useState(true); // Set to false to hide debug overlay

  // --- Region Editor State ---
  const [editMode, setEditMode] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [pendingRects, setPendingRects] = useState<Array<{ x1: number; y1: number; x2: number; y2: number }>>([]);
  const [regionName, setRegionName] = useState('');
  const [regionType, setRegionType] = useState('Kanto');
  const [regionsData, setRegionsData] = useState<MapRegionsData>({});
  const [flatRegions, setFlatRegions] = useState<MapRegion[]>([]);
  const [hoveredExistingRegion, setHoveredExistingRegion] = useState<{ region: MapRegion; mousePos: { x: number; y: number } } | null>(null);

  // Load regions from JSON file on component mount
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await fetch('/mapRegions.json');
        if (response.ok) {
          const loadedRegions: MapRegionsData = await response.json();
          setRegionsData(loadedRegions);

          // Flatten regions for easier use in map areas and editor
          const flattened: MapRegion[] = [];
          Object.entries(loadedRegions).forEach(([groupName, group]) => {
            group.regions.forEach(region => {
              flattened.push({
                ...region,
                // Store the group info for color mapping
                groupName,
                groupColor: group.color
              } as MapRegion & { groupName: string; groupColor: string });
            });
          });
          setFlatRegions(flattened);
        } else {
          console.error('Failed to load mapRegions.json file');
          setRegionsData({});
          setFlatRegions([]);
        }
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsData({});
        setFlatRegions([]);
      }
    };

    loadRegions();
  }, []);

  // Helper function to check if a point is within a rectangular region
  const isPointInRegion = (x: number, y: number, region: MapRegion): boolean => {
    if (region.shape !== 'rect' || !region.coords) return false;
    const coords = region.coords.split(',').map(Number);
    if (coords.length !== 4) return false;
    const [x1, y1, x2, y2] = coords;
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    return x >= minX && x <= maxX && y >= minY && y <= maxY;
  };

  // --- Drawing Handlers ---
  const handleEditorMouseDown = (e: React.MouseEvent) => {
    if (!editMode) return;
    // Only left mouse button (button === 0) triggers drawing
    if (e.button !== 0) return;
    const container = e.currentTarget;
    const image = container.querySelector('.world-map') as HTMLImageElement;
    if (!image) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Get mouse position relative to container
    const containerX = e.clientX - containerRect.left;
    const containerY = e.clientY - containerRect.top;

    // Get mouse position relative to the transformed image
    const imageX = e.clientX - imageRect.left;
    const imageY = e.clientY - imageRect.top;

    // Convert to natural image coordinates (accounting for scale)
    const x = (imageX / imageRect.width) * image.naturalWidth;
    const y = (imageY / imageRect.height) * image.naturalHeight;

    setStart({ x, y });
    setDrawing(true);
    setCurrentRect({ x1: x, y1: y, x2: x, y2: y });
  };

  const handleEditorMouseMove = (e: React.MouseEvent) => {
    if (!editMode || !drawing || !start) return;
    const container = e.currentTarget;
    const image = container.querySelector('.world-map') as HTMLImageElement;
    if (!image) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Get mouse position relative to container
    const containerX = e.clientX - containerRect.left;
    const containerY = e.clientY - containerRect.top;

    // Get mouse position relative to the transformed image
    const imageX = e.clientX - imageRect.left;
    const imageY = e.clientY - imageRect.top;

    // Convert to natural image coordinates (accounting for scale)
    const x = (imageX / imageRect.width) * image.naturalWidth;
    const y = (imageY / imageRect.height) * image.naturalHeight;

    setCurrentRect({ x1: start.x, y1: start.y, x2: x, y2: y });
  };

  const handleEditorMouseUp = () => {
    if (currentRect) {
      setPendingRects([...pendingRects, currentRect]);
      setCurrentRect(null);
    }
    setDrawing(false);
  };

  // --- Add/Export Region ---
  const handleAddRegion = () => {
    if (pendingRects.length === 0 || !regionName) return;
    // Add all rectangles as a single region (multi-rect support)
    const newRegions = pendingRects.map(rect => {
      const { x1, y1, x2, y2 } = rect;
      const coords = `${Math.round(x1)},${Math.round(y1)},${Math.round(x2)},${Math.round(y2)}`;
      return {
        name: regionName,
        coords,
        shape: "rect" as "rect",
        subzones: null,
        groupName: regionType,
        groupColor: regionsData[regionType]?.color || '#feca57'
      };
    });
    setFlatRegions([...flatRegions, ...newRegions]);
    setPendingRects([]);
    setRegionName('');
  };

  const handleExport = () => {
    // Convert flat regions back to grouped structure for export
    const grouped: MapRegionsData = {};

    flatRegions.forEach(region => {
      const groupName = region.groupName || 'Kanto';
      if (!grouped[groupName]) {
        grouped[groupName] = {
          color: region.groupColor || '#feca57',
          regions: []
        };
      }
      grouped[groupName].regions.push({
        name: region.name,
        coords: region.coords,
        shape: region.shape,
        subzones: region.subzones
      });
    });

    const json = JSON.stringify(grouped, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mapRegions.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clear existing region hover when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      setHoveredExistingRegion(null);
    }
  }, [editMode]);

  // Prevent page scrolling when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Calculate minimum scale to fit image in viewport
  useEffect(() => {
    const calculateMinScale = () => {
      const container = document.querySelector('.map-zoom-container') as HTMLElement;
      const image = document.querySelector('.world-map') as HTMLImageElement;

      if (!container || !image || !image.naturalWidth) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;

      // Calculate scale needed to fit image in container
      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;
      const minRequiredScale = Math.max(scaleX, scaleY);

      setMinScale(minRequiredScale);

      // If current scale is below minimum, reset it
      if (scale < minRequiredScale) {
        setScale(minRequiredScale);
        setPanOffset({ x: 0, y: 0 });
      }
    };

    // Calculate on mount and when window resizes
    const handleResize = () => calculateMinScale();
    window.addEventListener('resize', handleResize);

    // Also calculate when image loads
    const image = document.querySelector('.world-map') as HTMLImageElement;
    if (image) {
      if (image.complete) {
        calculateMinScale();
      } else {
        image.onload = calculateMinScale;
      }
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [scale]);

  // Fetch spawn data for selected region
  const fetchSpawnData = async (regionName: string) => {
    setLoading(true);
    try {
      // Use the backend API to search spawns — avoids downloading 1.8 MB of raw JSON
      const response = await fetch('/api/spawns/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: regionName, type: 'map' }),
      });

      if (!response.ok) throw new Error(`Spawn API returned ${response.status}`);

      const data = await response.json();
      // Backend already tags spawns with _source: 'land' | 'surf'
      const filteredSpawns = (data.spawns as SpawnData[]).filter((spawn) =>
        spawn.Map && spawn.Map.toLowerCase() === regionName.toLowerCase()
      );
      setSpawnData(filteredSpawns || []);
    } catch (error) {
      console.error('Failed to fetch spawn data:', error);
      setSpawnData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegionHover = (regionName: string, event: React.MouseEvent) => {
    setHoveredRegion(regionName);
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleRegionLeave = () => {
    setHoveredRegion(null);
  };

  const handleRegionClick = (regionName: string) => {
    setSelectedRegion(regionName);
    fetchSpawnData(regionName);
  };

  const closePopup = () => {
    setSelectedRegion(null);
    setSpawnData([]);
  };

  const navigate = useNavigate();

  const handleBackToSite = () => {
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Debug function to track mouse coordinates relative to the image
  const handleDebugMouseMove = (e: React.MouseEvent) => {
    const image = e.currentTarget.querySelector('.world-map') as HTMLImageElement;
    if (!image) return;

    const rect = image.getBoundingClientRect();
    const imageX = e.clientX - rect.left;
    const imageY = e.clientY - rect.top;

    // Calculate coordinates relative to the original image size (accounting for scale and pan)
    const scaleAdjustedX = imageX / scale;
    const scaleAdjustedY = imageY / scale;

    setDebugCoords({
      x: Math.round(scaleAdjustedX),
      y: Math.round(scaleAdjustedY)
    });
  };

  const getRegionColor = (regionName: string) => {
    const region = flatRegions.find((r: MapRegion) => r.name === regionName);
    return region?.groupColor || '#feca57';
  };

  // Zoom functions
  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5);
    setScale(newScale);
    // Re-constrain pan offset after zoom change
    const constrained = constrainPan(panOffset.x, panOffset.y, newScale);
    setPanOffset(constrained);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale / 1.2, minScale);
    setScale(newScale);
    // Re-constrain pan offset after zoom change
    const constrained = constrainPan(panOffset.x, panOffset.y, newScale);
    setPanOffset(constrained);
  };

  // Helper function to constrain pan within image bounds
  const constrainPan = (x: number, y: number, currentScale: number) => {
    const container = document.querySelector('.map-zoom-container') as HTMLElement;
    const image = document.querySelector('.world-map') as HTMLImageElement;

    if (!container || !image || !image.naturalWidth) return { x: 0, y: 0 };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const imageWidth = image.naturalWidth * currentScale;
    const imageHeight = image.naturalHeight * currentScale;

    // If image is smaller than container, center it (no panning allowed)
    if (imageWidth <= containerWidth && imageHeight <= containerHeight) {
      return { x: 0, y: 0 };
    }

    // Calculate maximum pan distances to keep image edges within container
    let maxPanX = 0;
    let maxPanY = 0;

    if (imageWidth > containerWidth) {
      maxPanX = (imageWidth - containerWidth) / 2;
    }

    if (imageHeight > containerHeight) {
      maxPanY = (imageHeight - containerHeight) / 2;
    }

    // Constrain the pan values
    const constrainedX = Math.max(-maxPanX, Math.min(maxPanX, x));
    const constrainedY = Math.max(-maxPanY, Math.min(maxPanY, y));

    return { x: constrainedX, y: constrainedY };
  };

  // Pan functions
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'IMG') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const newX = e.clientX - panStart.x;
      const newY = e.clientY - panStart.y;
      const constrained = constrainPan(newX, newY, scale);
      setPanOffset(constrained);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsPanning(true);
      setPanStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isPanning && e.touches.length === 1) {
      const touch = e.touches[0];
      const newX = touch.clientX - panStart.x;
      const newY = touch.clientY - panStart.y;
      const constrained = constrainPan(newX, newY, scale);
      setPanOffset(constrained);
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(minScale, Math.min(5, scale * delta));
    setScale(newScale);
    // Re-constrain pan offset after zoom change
    const constrained = constrainPan(panOffset.x, panOffset.y, newScale);
    setPanOffset(constrained);
  };

  return (
    <div className="interactive-map-container">
      {/* Edit mode toggle (for region editor) */}
      <button
        className={`edit-mode-toggle${editMode ? ' active' : ''}`}
        style={{ position: 'absolute', top: 10, right: 120, zIndex: 1001 }}
        onClick={() => setEditMode(!editMode)}
      >
        {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
      </button>

      {/* Sidebar toggle button */}
      <button
        className={`sidebar-toggle ${sidebarOpen ? 'open' : ''}`}
        onClick={toggleSidebar}
      >
        {sidebarOpen ? '←' : '→'}
      </button>

      {/* Left sidebar navigation */}
      <div className={`sidebar-nav ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="sidebar-title">PRO Map Region Editor</div>
            <div className="sidebar-subtitle">Pokemon Revolution Online</div>
          </div>

          <div className="sidebar-section">
            <h3>Navigation</h3>
            <button className="back-btn" onClick={handleBackToSite}>
              ← Back to PRO Market History
            </button>
          </div>

          <div className="sidebar-section">
            <h3>Instructions</h3>
            <div className="sidebar-info">
              • Click on any region to see Pokemon spawns<br/>
              • Use mouse wheel to zoom in/out<br/>
              • Click and drag to pan around<br/>
              • Use zoom buttons (+/-) on the right<br/>
              • Touch gestures work on mobile devices
              {editMode && (
                <>
                  <br/>• <strong>Edit Mode:</strong> Left click to draw rectangles<br/>
                  • Right click to pan while in edit mode
                </>
              )}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Debug Tools</h3>
            <button
              className="back-btn"
              onClick={() => setShowDebug(!showDebug)}
              style={{ marginBottom: '10px' }}
            >
              {showDebug ? 'Hide' : 'Show'} Coordinates
            </button>
            <div className="sidebar-info">
              Toggle coordinate display to help map clickable regions.
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Regions</h3>
            <div className="sidebar-info">
              Explore Kanto, Johto, Hoenn, and Sinnoh regions. Click on cities, routes, and special areas to discover Pokemon spawns, levels, and rarities.
            </div>
          </div>
        </div>
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={handleZoomIn}>+</button>
        <button className="zoom-btn" onClick={handleZoomOut}>−</button>
      </div>

      <div className="map-wrapper">
        <div className="map-image-container">
          <div
            className="map-zoom-container"
            onMouseDown={(e) => {
              if (editMode) {
                if (e.button === 0) {
                  // Left click draws rectangle
                  handleEditorMouseDown(e);
                } else if (e.button === 2) {
                  // Right click pans in edit mode
                  e.preventDefault();
                  handleMouseDown(e);
                }
              } else {
                // In normal mode, both left and right click pan
                handleMouseDown(e);
              }
            }}
            onMouseMove={(e) => {
              if (editMode) {
                handleEditorMouseMove(e);

                // Check for hovering over existing regions
                if (!drawing) {
                  const container = e.currentTarget;
                  const image = container.querySelector('.world-map') as HTMLImageElement;
                  if (image) {
                    const imageRect = image.getBoundingClientRect();
                    const imageX = e.clientX - imageRect.left;
                    const imageY = e.clientY - imageRect.top;
                    const x = (imageX / imageRect.width) * image.naturalWidth;
                    const y = (imageY / imageRect.height) * image.naturalHeight;

                    // Find hovered region
                    const hoveredRegion = flatRegions.find((region: MapRegion) =>
                      region.coords && isPointInRegion(x, y, region)
                    );

                    if (hoveredRegion) {
                      setHoveredExistingRegion({
                        region: hoveredRegion,
                        mousePos: { x: e.clientX, y: e.clientY }
                      });
                    } else {
                      setHoveredExistingRegion(null);
                    }
                  }
                }
              }

              if (isPanning) {
                handleMouseMove(e);
              }

              if (!editMode && !isPanning) {
                handleDebugMouseMove(e);
              }
            }}
            onMouseUp={() => {
              if (editMode) {
                handleEditorMouseUp();
              }

              handleMouseUp();
            }}
            onMouseLeave={() => {
              if (editMode) {
                handleEditorMouseUp();
                setHoveredExistingRegion(null);
              }

              handleMouseUp();
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              cursor: editMode ? (drawing ? 'crosshair' : 'pointer') : (isPanning ? 'grabbing' : 'grab')
            }}
          >
            <img
              src="/images/pro_world_map.png"
              alt="Pokemon Revolution Online World Map"
              className="world-map"
              useMap="#pro-world-map"
              style={{
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${scale})`,
                transformOrigin: 'center center'
              }}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
            />

            {/* Rectangle overlays for edit mode */}
            {editMode && (
              <svg
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 1002
                }}
              >
                {/* Existing regions overlay */}
                {flatRegions.filter((region: MapRegion) => region.coords && region.shape === 'rect').map((region, idx) => {
                  const image = document.querySelector('.world-map') as HTMLImageElement;
                  if (!image) return null;
                  const imageRect = image.getBoundingClientRect();
                  const container = document.querySelector('.map-zoom-container') as HTMLElement;
                  if (!container) return null;
                  const containerRect = container.getBoundingClientRect();

                  const coords = region.coords.split(',').map(Number);
                  if (coords.length !== 4) return null;
                  const [x1, y1, x2, y2] = coords;

                  // Calculate scale factor from natural size to current displayed size
                  const scaleX = imageRect.width / image.naturalWidth;
                  const scaleY = imageRect.height / image.naturalHeight;

                  // Position relative to container
                  const x = (imageRect.left - containerRect.left) + (Math.min(x1, x2) * scaleX);
                  const y = (imageRect.top - containerRect.top) + (Math.min(y1, y2) * scaleY);

                  const isHovered = hoveredExistingRegion?.region === region;

                  return (
                    <rect
                      key={`existing-${idx}`}
                      x={x}
                      y={y}
                      width={Math.abs(x2 - x1) * scaleX}
                      height={Math.abs(y2 - y1) * scaleY}
                      fill={isHovered ? "rgba(255,165,0,0.3)" : "rgba(0,128,255,0.15)"}
                      stroke={isHovered ? "#ffa500" : "#0080ff"}
                      strokeWidth={isHovered ? 3 : 1}
                      strokeDasharray={isHovered ? "none" : "5,5"}
                    />
                  );
                })}

                {/* Pending rectangles */}
                {pendingRects.map((rect, idx) => {
                  const image = document.querySelector('.world-map') as HTMLImageElement;
                  if (!image) return null;
                  const imageRect = image.getBoundingClientRect();
                  const container = document.querySelector('.map-zoom-container') as HTMLElement;
                  if (!container) return null;
                  const containerRect = container.getBoundingClientRect();

                  // Calculate scale factor from natural size to current displayed size
                  const scaleX = imageRect.width / image.naturalWidth;
                  const scaleY = imageRect.height / image.naturalHeight;

                  // Position relative to container
                  const x = (imageRect.left - containerRect.left) + (Math.min(rect.x1, rect.x2) * scaleX);
                  const y = (imageRect.top - containerRect.top) + (Math.min(rect.y1, rect.y2) * scaleY);

                  return (
                    <rect
                      key={"pending-" + idx}
                      x={x}
                      y={y}
                      width={Math.abs(rect.x2 - rect.x1) * scaleX}
                      height={Math.abs(rect.y2 - rect.y1) * scaleY}
                      fill="rgba(0,255,255,0.2)"
                      stroke="#0ff"
                      strokeWidth={2}
                    />
                  );
                })}
                {/* Current rectangle while dragging */}
                {drawing && currentRect && (() => {
                  const image = document.querySelector('.world-map') as HTMLImageElement;
                  if (!image) return null;
                  const imageRect = image.getBoundingClientRect();
                  const container = document.querySelector('.map-zoom-container') as HTMLElement;
                  if (!container) return null;
                  const containerRect = container.getBoundingClientRect();

                  // Calculate scale factor from natural size to current displayed size
                  const scaleX = imageRect.width / image.naturalWidth;
                  const scaleY = imageRect.height / image.naturalHeight;

                  // Position relative to container
                  const x = (imageRect.left - containerRect.left) + (Math.min(currentRect.x1, currentRect.x2) * scaleX);
                  const y = (imageRect.top - containerRect.top) + (Math.min(currentRect.y1, currentRect.y2) * scaleY);

                  return (
                    <rect
                      x={x}
                      y={y}
                      width={Math.abs(currentRect.x2 - currentRect.x1) * scaleX}
                      height={Math.abs(currentRect.y2 - currentRect.y1) * scaleY}
                      fill="rgba(255,255,0,0.3)"
                      stroke="#ff0"
                      strokeWidth={2}
                    />
                  );
                })()}
              </svg>
            )}

            <map name="pro-world-map">
              {flatRegions.map((region, index) => (
                <area
                  key={`${region.name}-${index}`}
                  shape={region.shape}
                  coords={region.coords}
                  alt={region.name}
                  title={region.name}
                  onMouseEnter={(e) => handleRegionHover(region.name, e)}
                  onMouseLeave={handleRegionLeave}
                  onClick={() => handleRegionClick(region.name)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </map>

            {/* Hover tooltip */}
            {hoveredRegion && (
              <div
                className="map-tooltip"
                style={{
                  position: 'fixed',
                  left: mousePosition.x + 10,
                  top: mousePosition.y - 10,
                  backgroundColor: getRegionColor(hoveredRegion),
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                {hoveredRegion}
              </div>
            )}

            {/* Edit mode existing region tooltip */}
            {editMode && hoveredExistingRegion && (
              <div
                className="map-tooltip"
                style={{
                  position: 'fixed',
                  left: hoveredExistingRegion.mousePos.x + 15,
                  top: hoveredExistingRegion.mousePos.y - 15,
                  backgroundColor: '#ff6600',
                  color: 'white',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  zIndex: 1001,
                  boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
                  maxWidth: '200px'
                }}
              >
                <div style={{ marginBottom: '4px' }}>{hoveredExistingRegion.region.name}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>
                  Group: {hoveredExistingRegion.region.groupName}<br/>
                  Coords: {hoveredExistingRegion.region.coords}
                </div>
              </div>
            )}

            {/* Debug coordinates overlay */}
            {showDebug && (
              <div className="debug-overlay">
                <div className="debug-coords">
                  <strong>Debug Coordinates:</strong><br/>
                  X: {debugCoords.x}<br/>
                  Y: {debugCoords.y}<br/>
                  <small>Scale: {scale.toFixed(2)}x</small>
                </div>
                <div className="debug-toggle">
                  <button
                    onClick={() => setShowDebug(!showDebug)}
                    className="debug-toggle-btn"
                  >
                    Hide Debug
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Region editor controls (only in edit mode) */}
      {editMode && (
        <div
          style={{
            position: 'absolute',
            top: 60,
            right: 20,
            background: 'rgba(30,30,30,0.95)',
            color: '#fff',
            padding: '16px',
            borderRadius: '8px',
            zIndex: 1003,
            minWidth: '320px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          <h3>Map Region Editor</h3>
          <div style={{ marginBottom: 10 }}>
            <label>
              Region Name:
              <input value={regionName} onChange={e => setRegionName(e.target.value)} style={{ marginLeft: 8 }} />
            </label>
            <label style={{ marginLeft: 16 }}>
              Region Group:
              <select value={regionType} onChange={e => setRegionType(e.target.value)} style={{ marginLeft: 8 }}>
                <option value="Easter">Easter</option>
                <option value="Summer">Summer</option>
                <option value="Christmas">Christmas</option>
                <option value="Halloween">Halloween</option>
                <option value="Vday">Vday</option>
                <option value="Pinkan">Pinkan</option>
                <option value="SevenIslands">Seven Islands</option>
                <option value="Kanto">Kanto</option>
              </select>
            </label>
            <button onClick={handleAddRegion} style={{ marginLeft: 16 }}>Add Region</button>
            <button onClick={handleExport} style={{ marginLeft: 8 }}>Export JSON</button>
            <button onClick={() => setPendingRects(pendingRects.slice(0, -1))} style={{ marginLeft: 8 }} disabled={pendingRects.length === 0}>Delete Last</button>
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', background: '#222', padding: '8px', borderRadius: '6px' }}>
            <h4>Regions ({flatRegions.length})</h4>
            <ul style={{ fontSize: '0.85em', paddingLeft: '18px', margin: 0 }}>
              {flatRegions.slice(-10).map((r, i) => (
                <li key={i}>{r.name} ({r.groupName}): {r.coords}</li>
              ))}
              {flatRegions.length > 10 && <li><em>...and {flatRegions.length - 10} more</em></li>}
            </ul>
          </div>
          <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#aaa' }}>
            <b>Instructions:</b> Left click and drag to draw rectangles. Right click to pan. Name and add regions. Export JSON when done.
          </div>
        </div>
      )}

      {/* Spawn data popup */}
      {selectedRegion && (
        <div className="spawn-popup-overlay" onClick={closePopup}>
          <div className="spawn-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2>{selectedRegion}</h2>
              <button className="close-button" onClick={closePopup}>×</button>
            </div>

            <div className="popup-content">
              {loading ? (
                <div className="loading">Loading spawn data...</div>
              ) : spawnData.length > 0 ? (
                <div className="spawn-list">
                  <div className="spawn-summary">
                    <p><strong>{spawnData.length}</strong> Pokemon found</p>
                  </div>

                  <div className="spawn-grid">
                    {spawnData.map((spawn, index) => {
                      const source = (spawn as SpawnData & { _source?: string })._source;
                      return (
                        <div key={index} className={`spawn-item ${spawn.Tier.toLowerCase()}`}>
                          <div className="pokemon-name">
                            {spawn.Pokemon}
                            {spawn.MemberOnly && <span className="ms-badge">MS</span>}
                          </div>
                          <div className="pokemon-details">
                            <span className="level-range">Lv. {spawn.MinLVL}-{spawn.MaxLVL}</span>
                            <span className={`rarity ${spawn.Tier.toLowerCase()}`}>{spawn.Tier}</span>
                            <span className="spawn-type-badge" style={{
                              background: source === 'surf' ? '#2196f3' : '#43a047',
                              color: '#fff',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              fontSize: '0.85rem',
                              marginLeft: '8px',
                              fontWeight: 600
                            }}>
                              {source === 'surf' ? 'Surf' : 'Land'}
                            </span>
                          </div>
                          {spawn.Item && (
                            <div className="pokemon-item">Item: {spawn.Item}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-spawns">
                  <p>No spawn data available for this region.</p>
                  <p><small>This might be a city or special area without wild Pokemon.</small></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapRegionEditor;
